import React from "react";
import airtable from "../../lib/airtables";
import { useAuth } from "../../auth/AuthProvider";
import { resourceSubmissionFieldIds } from "../../lib/airtables/tables/ResourceSubmissions";
import { teacherFieldIds } from "../../lib/airtables/tables/Teachers";

const CONSENT_SHARE_FIELD = resourceSubmissionFieldIds.consentToShareResource;
const CONSENT_INCLUDE_NAME_FIELD = resourceSubmissionFieldIds.consentToIncludeName;
const CONSENT_SHARE_LABEL =
  "I created this resource and/or have permission to allow DWF to share this resource with other Legacy School Educators. Or, this resource is publicly available";
const CONSENT_INCLUDE_NAME_LABEL =
  "I would like DWF to include my name as authorship on this resource";

const getNameParts = (name?: string) => {
  if (!name) return { firstName: "", lastName: "" };
  const parts = name.trim().split(/\s+/);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" ")
  };
};

export default function ResourceSubmissionPage() {
  const { user } = useAuth();
  const [statusMessage, setStatusMessage] = React.useState("");
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [step, setStep] = React.useState<1 | 2>(1);
  const [errors, setErrors] = React.useState<Record<string, string>>({});
  const [formData, setFormData] = React.useState(() => ({
    firstName: "",
    lastName: "",
    email: "",
    consentShare: false,
    consentIncludeName: false
  }));

  React.useEffect(() => {
    if (!user) return;
    const nameParts = getNameParts(
      user?.[teacherFieldIds.name]
    );
    setFormData(prev => ({
      ...prev,
      firstName: prev.firstName || nameParts.firstName,
      lastName: prev.lastName || nameParts.lastName,
      email: prev.email || user?.[teacherFieldIds.email] || ""
    }));
  }, [user]);

  const handleChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormData(prev => ({
      ...prev,
      [name]: checked
    }));
  };

  const validate = () => {
    const nextErrors: Record<string, string> = {};
    if (!formData.firstName.trim()) nextErrors.firstName = "First name is required.";
    if (!formData.lastName.trim()) nextErrors.lastName = "Last name is required.";
    if (!formData.email.trim()) nextErrors.email = "Email is required.";
    if (!formData.consentShare) {
      nextErrors.consentShare = "You must confirm permission to share.";
    }
    return nextErrors;
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setStatusMessage("");
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) {
      setStatusMessage("Please fix the highlighted fields and try again.");
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: Record<string, any> = {
        [resourceSubmissionFieldIds.firstName]: formData.firstName.trim(),
        [resourceSubmissionFieldIds.lastName]: formData.lastName.trim(),
        [resourceSubmissionFieldIds.email]: formData.email.trim(),
        [CONSENT_SHARE_FIELD]: formData.consentShare,
        [CONSENT_INCLUDE_NAME_FIELD]: formData.consentIncludeName,
        [resourceSubmissionFieldIds.submissionTime]: new Date().toISOString()
      };

      await airtable.resourceSubmissions.create(payload);

      setStatusMessage("Thanks! Your information has been received.");
      setErrors({});
      setStep(2);
    } catch (error: any) {
      setStatusMessage(
        error?.message || "Something went wrong while submitting your resource. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="page bg-white">
      <div className="max-w-4xl mx-auto my-12 px-4">
        <h1 className="text-3xl font-bold mb-4">Submit a Resource to Share</h1>
        <p className="mb-8 text-gray-700">
          Share a resource with the DWF community. Required fields are marked with an
          asterisk.
        </p>
        {step === 1 ? (
          <form
            onSubmit={handleSubmit}
            className="space-y-6 rounded-lg bg-white p-6 shadow"
          >
            <div>
              <h2 className="text-xl font-semibold mb-4">Your Information</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <label className="block text-sm font-medium text-gray-700">
                  First Name<span className="text-red-500">*</span>
                  <input
                    className="mt-2 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                    name="firstName"
                    maxLength={1500}
                    value={formData.firstName}
                    onChange={handleChange}
                  />
                  {errors.firstName && (
                    <span className="mt-2 block text-sm text-red-500">
                      {errors.firstName}
                    </span>
                  )}
                </label>
                <label className="block text-sm font-medium text-gray-700">
                  Last Name<span className="text-red-500">*</span>
                  <input
                    className="mt-2 w-full rounded-md border border-gray-200 px-3 py-2 text-sm"
                    name="lastName"
                    maxLength={1500}
                    value={formData.lastName}
                    onChange={handleChange}
                  />
                  {errors.lastName && (
                    <span className="mt-2 block text-sm text-red-500">
                      {errors.lastName}
                    </span>
                  )}
                </label>
                <label className="block text-sm font-medium text-gray-700 md:col-span-2">
                  Email<span className="text-red-500">*</span>
                  <input
                    className="mt-2 w-full rounded-md border border-gray-200 bg-gray-100 px-3 py-2 text-sm"
                    name="email"
                    maxLength={1500}
                    value={formData.email}
                    onChange={handleChange}
                    readOnly
                  />
                  {errors.email && (
                    <span className="mt-2 block text-sm text-red-500">
                      {errors.email}
                    </span>
                  )}
                </label>
              </div>
            </div>

            <div>
              <h2 className="text-xl font-semibold mb-4">Consent to Share</h2>
              <div className="space-y-3">
                <label className="flex items-start gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    name="consentShare"
                    className="mt-1"
                    checked={formData.consentShare}
                    onChange={handleCheckboxChange}
                  />
                  <span>
                    {CONSENT_SHARE_LABEL}
                    <span className="text-red-500">*</span>
                  </span>
                </label>
                {errors.consentShare && (
                  <span className="block text-sm text-red-500">
                    {errors.consentShare}
                  </span>
                )}
                <label className="flex items-start gap-3 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    name="consentIncludeName"
                    className="mt-1"
                    checked={formData.consentIncludeName}
                    onChange={handleCheckboxChange}
                  />
                  <span>
                    {CONSENT_INCLUDE_NAME_LABEL}
                    <span className="block text-xs text-gray-500 mt-1">
                      If unchecked, we will not attach your name to the resource you have
                      submitted.
                    </span>
                  </span>
                </label>
              </div>
            </div>

            {statusMessage && (
              <p className="text-sm text-blue-600">{statusMessage}</p>
            )}

            <button
              type="submit"
              className="inline-flex items-center rounded-md token-btn-primary px-4 py-2 text-white disabled:opacity-50"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Next"}
            </button>
          </form>
        ) : (
          <div className="space-y-4 rounded-lg bg-white p-6 shadow">
            <h2 className="text-xl font-semibold">Resource Submission Form</h2>
            <p className="text-sm text-gray-600">
              Please complete the Airtable form below to upload your resource files and details.
            </p>
            <iframe
              title="Resource Submission"
              src="https://airtable.com/embed/apprWesJGBmsmfsWx/pagT5EIwCyVA0cByN/form"
              className="w-full rounded-md border border-gray-200"
              style={{ minHeight: "720px" }}
            />
          </div>
        )}
      </div>
    </main>
  );
}
